import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Container, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddRounded, DeleteOutline, DownloadRounded, PrintOutlined } from '@mui/icons-material';
import AsyncAutocomplete from '../components/AsyncAutocomplete';
import DataTable from '../components/DataTable';
import { fetchProducto, searchProductosLight } from '../api/productos';
import { useLoading } from '../contexts/LoadingContext';
import {
  buildBarcodeLabel, createBarcodePDF, DEFAULT_LABEL, downloadBarcodePNG, labelQuantity, MAX_LABELS, prepareBarcodeBatch,
} from '../utils/barcodeLabels';

export default function EtiquetasBarras() {
  const [params] = useSearchParams();
  const productoId = params.get('producto');
  const { stop } = useLoading();
  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const [quantity, setQuantity] = useState('1');
  const [size, setSize] = useState(DEFAULT_LABEL);
  const [previewId, setPreviewId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const printUrls = useRef([]);
  useEffect(() => { stop(); }, [stop]);
  useEffect(() => () => { printUrls.current.forEach((url) => URL.revokeObjectURL(url)); }, []);

  useEffect(() => {
    if (!productoId) return;
    let cancelled = false;
    setLoading(true);
    fetchProducto(productoId).then(({ data }) => {
      if (cancelled) return;
      const item = { ...data, quantity: 1 };
      setRows((current) => current.some((row) => String(row.id) === String(item.id)) ? current : [...current, item]);
      setPreviewId(item.id);
    }).catch((err) => {
      if (!cancelled) setError(err?.response?.data?.error || 'No se pudo cargar el producto.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productoId]);

  const fetchPage = useCallback(async ({ q, page, limit }) => {
    try {
      const result = await searchProductosLight({ q, page, pageSize: limit });
      return { ...result, items: (result.items || []).map((item) => ({
        ...item, label: `${item.nombre} · ${item.sku || 'Sin SKU'}`,
      })) };
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudieron buscar productos.');
      return { items: [], hasMore: false };
    }
  }, []);

  const previewRow = rows.find((row) => String(row.id) === String(previewId)) || rows[0];
  const preview = useMemo(() => {
    if (!previewRow) return null;
    try { return { label: buildBarcodeLabel(previewRow.sku, size) }; }
    catch (err) { return { error: err.message }; }
  }, [previewRow, size]);
  const batchError = useMemo(() => {
    if (!rows.length) return '';
    try { prepareBarcodeBatch(rows, size); return ''; }
    catch (err) { return err.message; }
  }, [rows, size]);
  const count = rows.reduce((total, row) => total + (Number(row.quantity) || 0), 0);

  const addProduct = () => {
    setError('');
    if (!selected) { setError('Selecciona un producto del catálogo.'); return; }
    let copies;
    try { copies = labelQuantity(quantity); }
    catch (err) { setError(err.message); return; }
    const index = rows.findIndex((row) => String(row.id) === String(selected.id));
    const next = [...rows];
    const item = { ...selected, quantity: index < 0 ? copies : Number(next[index].quantity) + copies };
    if (index < 0) next.push(item);
    else next[index] = item;
    try {
      prepareBarcodeBatch(next, size);
      setRows(next);
      setPreviewId(item.id);
    } catch (err) { setError(err.message); }
  };

  const exportPDF = (print = false) => {
    setError('');
    try {
      const doc = createBarcodePDF(rows, size);
      if (!print) { doc.save('etiquetas_codigos_barras.pdf'); return; }
      doc.autoPrint();
      const url = URL.createObjectURL(doc.output('blob'));
      printUrls.current.push(url);
      const tab = window.open(url, '_blank');
      if (tab) tab.opener = null;
      else setError('El navegador bloqueó la ventana de impresión. Permite ventanas emergentes o descarga el PDF.');
    } catch (err) { setError(err.message); }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h4">Etiquetas de códigos de barras</Typography>
        <Typography color="text.secondary">
          Selecciona la joyería y cuántas bolsas vas a etiquetar. En el papel se imprimen únicamente las barras.
        </Typography>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <AsyncAutocomplete label="Producto" value={selected} onChange={setSelected} fetchPage={fetchPage}
                  disabled={loading} placeholder="Busca por nombre o SKU" />
              </Box>
              <TextField label="Cantidad de etiquetas" type="number" value={quantity}
                inputProps={{ min: 1, max: MAX_LABELS, step: 1 }} sx={{ width: { md: 180 } }}
                onChange={(event) => setQuantity(event.target.value)} />
              <Button variant="contained" startIcon={<AddRounded />} disabled={loading || !selected} onClick={addProduct}>Agregar</Button>
              <Button component={RouterLink} to="/productos/nuevo">Crear producto</Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Cada modelo usa el SKU guardado en Productos. Repite su etiqueta en todas las bolsas de ese modelo.
              Si aún no tiene código, guarda el producto para que el sistema le asigne uno.
            </Typography>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Paper sx={{ p: 2, flex: 1 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Tamaño de la etiqueta</Typography>
              <TextField select label="Formato" value={['58x30', '50x25', '40x20'].includes(`${size.width}x${size.height}`) ? `${size.width}x${size.height}` : ''}
                onChange={(event) => {
                  if (!event.target.value) return;
                  const [width, height] = event.target.value.split('x').map(Number);
                  setSize({ width, height });
                }}>
                <MenuItem value="58x30">58 × 30 mm</MenuItem>
                <MenuItem value="50x25">50 × 25 mm</MenuItem>
                <MenuItem value="40x20">40 × 20 mm</MenuItem>
                <MenuItem value="">Personalizado</MenuItem>
              </TextField>
              <Stack direction="row" spacing={2}>
                <TextField label="Ancho (mm)" type="number" value={size.width} fullWidth
                  inputProps={{ min: 20, max: 100, step: 1 }} onChange={(event) => setSize((current) => ({ ...current, width: event.target.value }))} />
                <TextField label="Alto (mm)" type="number" value={size.height} fullWidth
                  inputProps={{ min: 15, max: 100, step: 1 }} onChange={(event) => setSize((current) => ({ ...current, height: event.target.value }))} />
              </Stack>
              <Alert severity="info">
                Mide el ancho que tu impresora puede imprimir y ajusta el tamaño. Descarga una imagen PNG para la aplicación
                de la impresora, o imprime el PDF al 100 %, sin ajustar a la página. Prueba primero una etiqueta en el escáner de Ventas.
              </Alert>
            </Stack>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
            <Stack spacing={2} alignItems="center">
              <Typography variant="h6">Vista previa</Typography>
              {previewRow && <Typography variant="body2" color="text.secondary">
                {previewRow.nombre} · {previewRow.sku || 'Sin SKU'}
              </Typography>}
              {preview?.error && <Alert severity="warning">{preview.error}</Alert>}
              {preview?.label && <Box component="img" alt="Etiqueta que contiene únicamente el código de barras"
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(preview.label.svg)}`}
                sx={{ width: `${preview.label.width}mm`, maxWidth: '100%', height: 'auto', bgcolor: '#fff', border: '1px solid #ccc' }} />}
              {!previewRow && <Typography color="text.secondary">Agrega un producto para ver su etiqueta.</Typography>}
              <Button startIcon={<DownloadRounded />} disabled={!preview?.label} onClick={() => {
                try { downloadBarcodePNG(previewRow.sku, size); setError(''); }
                catch (err) { setError(err.message); }
              }}>Descargar esta etiqueta en PNG</Button>
            </Stack>
          </Paper>
        </Stack>

        {batchError && <Alert severity="warning">{batchError}</Alert>}
        <DataTable rows={rows} showActions={false} loading={loading} columns={[
          { Header: 'Producto', accessor: 'nombre' },
          { Header: 'Código asociado', accessor: 'sku' },
          { Header: 'Etiquetas', accessor: (row) => <TextField type="number" size="small" value={row.quantity}
            inputProps={{ min: 1, max: MAX_LABELS, step: 1, 'aria-label': `Etiquetas para ${row.nombre}` }} sx={{ width: 100 }}
            onChange={(event) => setRows((current) => current.map((item) => item.id === row.id ? { ...item, quantity: event.target.value } : item))} /> },
          { Header: 'Acciones', accessor: (row) => <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setPreviewId(row.id)}>Vista previa</Button>
            <Button size="small" component={RouterLink} to={`/productos/editar/${row.id}`}>Producto</Button>
            <Tooltip title="Quitar de la impresión"><IconButton aria-label={`Quitar ${row.nombre}`} color="error"
              onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))}><DeleteOutline /></IconButton></Tooltip>
          </Stack> },
        ]} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" alignItems={{ sm: 'center' }}>
          <Typography>{count} etiquetas · Una por página del PDF</Typography>
          <Button variant="outlined" startIcon={<DownloadRounded />} disabled={!rows.length || Boolean(batchError) || loading} onClick={() => exportPDF(false)}>Descargar PDF</Button>
          <Button variant="contained" startIcon={<PrintOutlined />} disabled={!rows.length || Boolean(batchError) || loading} onClick={() => exportPDF(true)}>Imprimir</Button>
        </Stack>
      </Stack>
    </Container>
  );
}
